/*
 * Social Login Configuration Component
 * 
 * Admin panel form for configuring Google and Facebook OAuth settings
 * Features:
 * - Google OAuth configuration (Client ID, Client Secret)
 * - Facebook OAuth configuration (App ID, App Secret)
 * - Enable/disable toggles for each provider
 * - Secure credential management
 * - Real-time validation
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';

const SocialLoginConfig = () => {
  const [config, setConfig] = useState({
    google_client_id: '',
    google_client_secret: '',
    facebook_app_id: '',
    facebook_app_secret: '',
    is_google_enabled: false,
    is_facebook_enabled: false
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSecrets, setShowSecrets] = useState({
    google_secret: false,
    facebook_secret: false
  });

  // Load current configuration
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`${API}/auth/social/admin/config`);
      setConfig(response.data);
    } catch (error) {
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setConfig(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${API}/auth/social/admin/config`, config);
      setSuccess('Social login configuration updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);

    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update configuration');
    } finally {
      setSaving(false);
    }
  };

  const toggleSecretVisibility = (field) => {
    setShowSecrets(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const isConfigValid = () => {
    const googleValid = config.is_google_enabled ?
      (config.google_client_id && config.google_client_secret &&
        config.google_client_id !== 'placeholder_google_client_id') : true;

    const facebookValid = config.is_facebook_enabled ?
      (config.facebook_app_id && config.facebook_app_secret &&
        config.facebook_app_id !== 'placeholder_facebook_app_id') : true;

    return googleValid && facebookValid;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="text-4xl mb-4">⚙️</div>
            <p className="text-gray-600">Loading social login configuration...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <span className="mr-3">🔐</span>
          Social Login Configuration
        </h2>
        <p className="text-gray-600 mt-1">
          Configure Google and Facebook OAuth settings for federated login
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Google OAuth Section */}
        <div className="border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <span className="text-2xl mr-3">🔍</span>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Google OAuth</h3>
                <p className="text-sm text-gray-600">Configure Google Sign-In</p>
              </div>
            </div>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_google_enabled"
                checked={config.is_google_enabled}
                onChange={handleInputChange}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">Enable Google Login</span>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Google Client ID
              </label>
              <input
                type="text"
                name="google_client_id"
                value={config.google_client_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter Google Client ID"
                disabled={!config.is_google_enabled}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Google Client Secret
              </label>
              <div className="relative">
                <input
                  type={showSecrets.google_secret ? 'text' : 'password'}
                  name="google_client_secret"
                  value={config.google_client_secret}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                  placeholder="Enter Google Client Secret"
                  disabled={!config.is_google_enabled}
                />
                <button
                  type="button"
                  onClick={() => toggleSecretVisibility('google_secret')}
                  className="absolute right-3 top-2 text-gray-500 hover:text-gray-700"
                  disabled={!config.is_google_enabled}
                >
                  {showSecrets.google_secret ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
          </div>

          {config.is_google_enabled && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm text-blue-800">
                <strong>Setup Instructions:</strong>
              </p>
              <ul className="text-sm text-blue-700 mt-1 space-y-1">
                <li>1. Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                <li>2. Create/select project → Enable Google+ API</li>
                <li>3. Create OAuth 2.0 credentials → Web application</li>
                <li>4. Add redirect URI: <code className="bg-blue-100 px-1 rounded">https://rideya.bz/auth/google/callback</code></li>
              </ul>
            </div>
          )}
        </div>

        {/* Facebook OAuth Section */}
        <div className="border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <span className="text-2xl mr-3">📘</span>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Facebook OAuth</h3>
                <p className="text-sm text-gray-600">Configure Facebook Login</p>
              </div>
            </div>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_facebook_enabled"
                checked={config.is_facebook_enabled}
                onChange={handleInputChange}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700">Enable Facebook Login</span>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Facebook App ID
              </label>
              <input
                type="text"
                name="facebook_app_id"
                value={config.facebook_app_id}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter Facebook App ID"
                disabled={!config.is_facebook_enabled}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Facebook App Secret
              </label>
              <div className="relative">
                <input
                  type={showSecrets.facebook_secret ? 'text' : 'password'}
                  name="facebook_app_secret"
                  value={config.facebook_app_secret}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                  placeholder="Enter Facebook App Secret"
                  disabled={!config.is_facebook_enabled}
                />
                <button
                  type="button"
                  onClick={() => toggleSecretVisibility('facebook_secret')}
                  className="absolute right-3 top-2 text-gray-500 hover:text-gray-700"
                  disabled={!config.is_facebook_enabled}
                >
                  {showSecrets.facebook_secret ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
          </div>

          {config.is_facebook_enabled && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
              <p className="text-sm text-blue-800">
                <strong>Setup Instructions:</strong>
              </p>
              <ul className="text-sm text-blue-700 mt-1 space-y-1">
                <li>1. Go to <a href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer" className="underline">Facebook Developers</a></li>
                <li>2. Create new app → Add Facebook Login product</li>
                <li>3. Configure settings → Add valid OAuth redirect URI</li>
                <li>4. Add redirect URI: <code className="bg-blue-100 px-1 rounded">https://rideya.bz/auth/facebook/callback</code></li>
              </ul>
            </div>
          )}
        </div>

        {/* Configuration Status */}
        <div className="border rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-3">Configuration Status</h4>
          <div className="space-y-2">
            <div className="flex items-center">
              <span className={`w-3 h-3 rounded-full mr-3 ${config.is_google_enabled && config.google_client_id &&
                config.google_client_secret && config.google_client_id !== 'placeholder_google_client_id'
                ? 'bg-green-500' : 'bg-gray-300'
                }`}></span>
              <span className="text-sm text-gray-700">Google OAuth: {
                config.is_google_enabled && config.google_client_id &&
                  config.google_client_secret && config.google_client_id !== 'placeholder_google_client_id'
                  ? 'Configured' : 'Not configured'
              }</span>
            </div>
            <div className="flex items-center">
              <span className={`w-3 h-3 rounded-full mr-3 ${config.is_facebook_enabled && config.facebook_app_id &&
                config.facebook_app_secret && config.facebook_app_id !== 'placeholder_facebook_app_id'
                ? 'bg-green-500' : 'bg-gray-300'
                }`}></span>
              <span className="text-sm text-gray-700">Facebook OAuth: {
                config.is_facebook_enabled && config.facebook_app_id &&
                  config.facebook_app_secret && config.facebook_app_id !== 'placeholder_facebook_app_id'
                  ? 'Configured' : 'Not configured'
              }</span>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving || !isConfigValid()}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 font-medium"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SocialLoginConfig;